// src/pages/Buyer/Enquiries.jsx
// ✅ FIXED:
// - Shows product name (fruit.name) not just "View Product" button
// - Shows updated_at for "Last Updated" column (was showing created_at twice)
// - Added invoice_sent status to filter options
// - enquiryAPI.cancelEnquiry uses correct method

import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress, Alert,
  IconButton, Menu, MenuItem,
} from '@mui/material';
import {
  Add, FilterList, MoreVert, Visibility, Chat, Payment,
  LocalShipping, Download, Cancel,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { enquiryAPI } from '../../services/api';
import { formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const BuyerEnquiries = () => {
  const navigate = useNavigate();
  const { id: urlId } = useParams();
  const [enquiries,       setEnquiries]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [filter,          setFilter]          = useState('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [detailDialog,    setDetailDialog]    = useState(false);
  const [anchorEl,        setAnchorEl]        = useState(null);
  const [selectedId,      setSelectedId]      = useState(null);

  useEffect(() => { fetchEnquiries(); }, []);

  // Auto-open detail dialog when navigating to /buyer/enquiries/:id
  useEffect(() => {
    if (urlId && enquiries.length > 0) {
      const found = enquiries.find(e => String(e.id) === String(urlId));
      if (found) { setSelectedEnquiry(found); setDetailDialog(true); }
    }
  }, [urlId, enquiries]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await enquiryAPI.getUserEnquiries();
      // ✅ handle both array and wrapped response
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.enquiries || []);
      setEnquiries(data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await enquiryAPI.cancelEnquiry(id);
      toast.success('Enquiry cancelled');
      fetchEnquiries();
    } catch {
      toast.error('Cannot cancel this enquiry');
    }
  };

  const statusOptions = [
    { value: 'all',          label: 'All Status' },
    { value: 'pending',      label: 'Pending' },
    { value: 'processing',   label: 'Processing' },
    { value: 'invoice_sent', label: 'Invoice Sent' },
    { value: 'confirmed',    label: 'Confirmed' },
    { value: 'cancelled',    label: 'Cancelled' },
    { value: 'completed',    label: 'Completed' },
  ];

  const filteredEnquiries = enquiries.filter(e =>
    filter === 'all' ? true : e.status === filter
  );

  const handleMenuOpen  = (e, id) => { setAnchorEl(e.currentTarget); setSelectedId(id); };
  const handleMenuClose = ()       => { setAnchorEl(null); setSelectedId(null); };

  const handleAction = (action) => {
    const enquiry = enquiries.find(e => e.id === selectedId);
    if (!enquiry) return;
    handleMenuClose();
    switch (action) {
      case 'view':    return navigate(`/buyer/enquiries/${selectedId}`);
      case 'chat':    return navigate(`/chat/${selectedId}`);
      case 'payment': return navigate(`/buyer/payment/${selectedId}`);
      case 'shipping':return navigate(`/buyer/shipping/${selectedId}`);
      case 'cancel':  return handleCancel(selectedId);
      default: break;
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading enquiries...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>My Enquiries</Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage all your fruit enquiries
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/fruits')}>
          New Enquiry
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FilterList color="action" />
          <Typography variant="body1">Filter:</Typography>
          {statusOptions.map((opt) => (
            <Chip key={opt.value} label={opt.label}
              onClick={() => setFilter(opt.value)}
              color={filter === opt.value ? 'primary' : 'default'}
              variant={filter === opt.value ? 'filled' : 'outlined'}
              clickable size="small" />
          ))}
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ p: 3 }}>
        {enquiries.length === 0 ? (
          <Alert severity="info">
            No enquiries yet.{' '}
            <Button size="small" onClick={() => navigate('/fruits')}>Browse Fruits</Button>
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['ID', 'Product', 'Qty', 'Date', 'Status', 'Last Updated', 'Actions'].map(h => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEnquiries.map((enquiry) => (
                  <TableRow key={enquiry.id} hover>
                    <TableCell>#{enquiry.enquiryId || enquiry.id}</TableCell>

                    {/* ✅ Show product name from fruit relationship */}
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {enquiry.product || enquiry.productName ||
                         enquiry.fruit?.name || `Fruit #${enquiry.fruit_id}`}
                      </Typography>
                      {enquiry.invoiceNumber && (
                        <Typography variant="caption" color="secondary.main">
                          Invoice: {enquiry.invoiceNumber}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>{enquiry.quantity} containers</TableCell>
                    <TableCell>{formatDate(enquiry.created_at || enquiry.createdAt)}</TableCell>

                    <TableCell>
                      <Chip label={enquiry.status || 'pending'}
                        color={getStatusColor(enquiry.status)} size="small"
                        sx={{ textTransform: 'capitalize' }} />
                    </TableCell>

                    {/* ✅ Last updated — use updated_at not created_at again */}
                    <TableCell>
                      {formatDate(enquiry.updated_at || enquiry.updatedAt || enquiry.created_at)}
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <IconButton size="small" title="View Details"
                          onClick={() => { setSelectedEnquiry(enquiry); setDetailDialog(true); }}>
                          <Visibility fontSize="small" />
                        </IconButton>

                        {enquiry.status !== 'cancelled' && enquiry.status !== 'completed' && (
                          <>
                            <IconButton size="small" color="primary" title="Chat"
                              onClick={() => navigate(`/chat/${enquiry.id}`)}>
                              <Chat fontSize="small" />
                            </IconButton>

                            {/* Payment button for invoice_sent or confirmed */}
                            {['invoice_sent', 'confirmed'].includes(enquiry.status) && (
                              <IconButton size="small" color="secondary" title="Pay Invoice"
                                onClick={() => navigate(`/buyer/payment/${enquiry.id}`)}>
                                <Payment fontSize="small" />
                              </IconButton>
                            )}

                            {['confirmed', 'shipped', 'in_transit'].includes(enquiry.status) && (
                              <IconButton size="small" color="info" title="Track Shipping"
                                onClick={() => navigate(`/buyer/shipping/${enquiry.id}`)}>
                                <LocalShipping fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )}

                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, enquiry.id)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filteredEnquiries.length === 0 && enquiries.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>No enquiries match the selected filter.</Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing {filteredEnquiries.length} of {enquiries.length} enquiries
        </Typography>
      </Paper>

      {/* More actions menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleAction('view')}><Visibility sx={{ mr: 1 }} fontSize="small" />View Details</MenuItem>
        <MenuItem onClick={() => handleAction('chat')}><Chat sx={{ mr: 1 }} fontSize="small" />Open Chat</MenuItem>
        <MenuItem onClick={() => handleAction('payment')}><Payment sx={{ mr: 1 }} fontSize="small" />Make Payment</MenuItem>
        <MenuItem onClick={() => handleAction('shipping')}><LocalShipping sx={{ mr: 1 }} fontSize="small" />Track Shipping</MenuItem>
        <MenuItem onClick={() => handleAction('cancel')} sx={{ color: 'error.main' }}>
          <Cancel sx={{ mr: 1 }} fontSize="small" />Cancel Enquiry
        </MenuItem>
      </Menu>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
        {selectedEnquiry && (
          <>
            <DialogTitle>Enquiry #{selectedEnquiry.enquiryId || selectedEnquiry.id}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {[
                  ['Status',   <Chip label={selectedEnquiry.status} color={getStatusColor(selectedEnquiry.status)} size="small" />],
                  ['Product',  selectedEnquiry.product || selectedEnquiry.fruit?.name || `Fruit #${selectedEnquiry.fruit_id}`],
                  ['Quantity', `${selectedEnquiry.quantity} containers`],
                  ['Created',  formatDate(selectedEnquiry.created_at)],
                  ['Invoice',  selectedEnquiry.invoiceNumber || 'None'],
                  selectedEnquiry.message ? ['Message', selectedEnquiry.message] : null,
                ].filter(Boolean).map(([label, value]) => (
                  <Box key={label} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ width: 90, flexShrink: 0 }}>
                      {label}:
                    </Typography>
                    {typeof value === 'string'
                      ? <Typography variant="body2">{value}</Typography>
                      : value}
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialog(false)}>Close</Button>
              <Button variant="contained"
                onClick={() => { setDetailDialog(false); navigate(`/buyer/enquiries/${selectedEnquiry.id}`); }}>
                Full Details
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default BuyerEnquiries;